import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle,
  PanelLeftClose,
  Share,
  Download,
  FolderInput,
  Trash2,
  X,
  LayoutGrid,
  ArrowDownUp,
  List,
} from 'lucide-react';
import { useLivePanel } from '../../live/context/LivePanelContext';
import { cn } from '../../../lib/utils';
import { RootState } from '../../../reducers';
import { useIsMobile } from '../../../hooks/use-mobile';
import { isSpecialFolder } from '../../../constants/folders';
import UpgradeButton from './UpgradeButton';
import UpsellButton from './UpsellButton';
import EducationalDiscount from './EducationalDiscount';
import AssistantToolbar from '../../assistant';
import { SidebarTrigger } from '../../../components/ui/sidebar';
import { AccessAvatarList } from '../../../components/shared/AccessAvatarList';
import { ShareDialog } from '../../../components/shared/ShareDialog';
import { Button } from '../../../components/ui/button';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '../../../components/ui/toggle-group';
import { useUserTags } from '../../../hooks/useTags';
import { useSelectMode } from '../../library/context/SelectModeContext';
import {
  deselectAllFiles,
  downloadMultipleFiles,
} from '../../library/librarySlice';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { setAlertMessage } from '../dashboardSlice';
import SortComponent from '../../library/actions/Sort';
import { LibraryViewType, FileType } from '../../../types';

// Spacer component that measures available space and shows share prompt if it fits
const SharePromptSpacer: React.FC<{
  show: boolean;
  onShareClick: () => void;
}> = ({ show, onShareClick }) => {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [hasSpace, setHasSpace] = React.useState(false);

  React.useEffect(() => {
    if (!show) {
      setHasSpace(false);
      return undefined;
    }

    const checkSpace = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const textWidth = textRef.current.scrollWidth;
        // Add some padding (32px) to ensure comfortable spacing
        setHasSpace(containerWidth >= textWidth + 32);
      }
    };

    checkSpace();

    const resizeObserver = new ResizeObserver(checkSpace);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [show]);

  return (
    <div
      ref={containerRef}
      className="flex-1 flex items-center justify-center min-w-0 overflow-hidden"
    >
      {show && (
        <span
          ref={textRef}
          className={cn(
            'text-sm text-muted-foreground whitespace-nowrap transition-opacity duration-150',
            hasSpace ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        >
          {t('postUploadShare.banner', 'Your recording is ready.')}{' '}
          <button
            type="button"
            onClick={onShareClick}
            className="font-medium text-foreground hover:underline"
          >
            {t('postUploadShare.bannerAction', 'Share it with someone')}
          </button>
        </span>
      )}
    </div>
  );
};

export const CustomToolbar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector(
    (state: RootState) => state.dashboard.drawerState === 'open',
  );
  const selectedVideo = useSelector((state: RootState) => state.selectedVideo);
  const selectedFolder = useSelector(
    (state: RootState) => state.library.selectedFolder,
  );
  const folders = useSelector((state: RootState) => state.library.folders.data);
  const currentUser = useSelector((state: RootState) => state.settings.user);
  const selectedFiles = useSelector(
    (state: RootState) => state.library.selectedFiles,
  );
  const libraryFiles = useSelector(
    (state: RootState) => state.library.files.data,
  );
  const isMobile = useIsMobile();
  const location = useLocation();
  const [isAssistantFocused, setIsAssistantFocused] = useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [hasReceivedInitialState, setHasReceivedInitialState] = useState(false);
  const [isToolbarShareDialogOpen, setIsToolbarShareDialogOpen] =
    useState(false);

  // Live panel context (only available on /live route)
  let livePanel: ReturnType<typeof useLivePanel> | null = null;
  try {
    livePanel = useLivePanel();
  } catch {
    // Not in LivePanelProvider context, that's ok
  }

  // Library toolbar state
  const { getTag, setTag } = useUserTags();
  const savedView = getTag('library-view-type') as LibraryViewType | null;
  const [view, setView] = useState<LibraryViewType>(savedView || 'grid');

  // Get select mode context safely (may not be available outside library)
  let setSelectMode: ((mode: boolean) => void) | null = null;
  try {
    const selectModeContext = useSelectMode();
    setSelectMode = selectModeContext.setSelectMode;
  } catch {
    // Not in SelectModeProvider context, that's ok
  }

  const showSelectedFileActions = Object.keys(selectedFiles).length > 0;
  const fileArray: FileType[] = Object.values(selectedFiles);
  const hasLibraryFiles = libraryFiles.length > 0;

  // Check if file is newly created (within last 5 minutes) for share prompt
  const isNewlyCreatedFile = React.useMemo(() => {
    if (!selectedVideo.file?.createdAt || !selectedVideo.file?._id)
      return false;

    // Check if we've already dismissed/interacted with share for this file
    const dismissedFiles = JSON.parse(
      localStorage.getItem('share-prompt-dismissed') || '[]',
    ) as string[];
    if (dismissedFiles.includes(selectedVideo.file._id)) return false;

    // Check if file was created within the last 5 minutes
    const createdAt = new Date(selectedVideo.file.createdAt).getTime();
    const now = Date.now();
    const fiveMinutesMs = 5 * 60 * 1000;
    return now - createdAt < fiveMinutesMs;
  }, [selectedVideo.file?.createdAt, selectedVideo.file?._id]);

  // Mark file as "share interacted" when share dialog is opened
  const handleShareClick = React.useCallback(() => {
    if (selectedVideo.file?._id) {
      const dismissedFiles = JSON.parse(
        localStorage.getItem('share-prompt-dismissed') || '[]',
      ) as string[];
      if (!dismissedFiles.includes(selectedVideo.file._id)) {
        dismissedFiles.push(selectedVideo.file._id);
        if (dismissedFiles.length > 100) dismissedFiles.shift();
        localStorage.setItem(
          'share-prompt-dismissed',
          JSON.stringify(dismissedFiles),
        );
      }
    }
    setIsToolbarShareDialogOpen(true);
  }, [selectedVideo.file?._id]);

  // Determine if we should hide sidebar button
  const shouldHideSidebarButton = isMobile && isAssistantFocused;

  // Show hamburger when on mobile and sidebar is not open
  const shouldShowHamburger = isMobile && !open && !shouldHideSidebarButton;

  // Check if we're in selected video view (same logic as in sidebar/ChatPanel)
  // Use hash for routes since app uses hash router
  const pathParts = (location.hash || location.pathname)
    .replace('#', '')
    .split('/')
    .filter(Boolean);
  const isSelectedVideoView =
    pathParts[0] === 'library' && pathParts.length === 4;
  const isLibraryFolderView =
    pathParts[0] === 'library' && pathParts.length === 3;
  const isLiveView = pathParts[0] === 'live';
  const isMobileViewport =
    typeof window !== 'undefined' && window.innerWidth < 768;

  // Get current folder from URL if selectedFolder is not set
  const currentFolderId = isLibraryFolderView ? pathParts[2] : null;
  const isSpecialView = currentFolderId
    ? isSpecialFolder(currentFolderId)
    : false;

  const currentFolder = React.useMemo(() => {
    if (selectedFolder) {
      return selectedFolder;
    }
    if (currentFolderId && folders.length > 0 && !isSpecialView) {
      return folders.find((f) => f._id === currentFolderId) || null;
    }
    return null;
  }, [selectedFolder, currentFolderId, folders, isSpecialView]);

  // Helper to check if current user can share
  const canShare = React.useMemo(() => {
    if (isSelectedVideoView && selectedVideo.file) {
      // For files, check if user is owner or if file is in a folder they own
      const { file } = selectedVideo;
      if (file.ownerId === currentUser?._id) return true;

      // If file is in a folder, check folder permissions
      if (currentFolder) {
        return currentFolder.ownerId === currentUser?._id;
      }
      return true; // Allow sharing if no folder context (will be validated by backend)
    }

    if (isLibraryFolderView && currentFolder) {
      // For folders, only owner can modify permissions
      if (isSpecialView) return false; // Can't share special folders
      return currentFolder.ownerId === currentUser?._id;
    }

    return false;
  }, [
    isSelectedVideoView,
    isLibraryFolderView,
    selectedVideo.file,
    currentFolder,
    isSpecialView,
    currentUser?._id,
  ]);

  // For special views (recents, private, etc.), show access list without a folder
  const shouldShowAccessList =
    isLibraryFolderView && (currentFolder || isSpecialView);

  // Chat panel will be open by default in selected video view on desktop
  const shouldChatPanelBeOpenByDefault =
    isSelectedVideoView && !isMobileViewport;

  // Check chat panel state on mount and when location changes
  React.useEffect(() => {
    // Reset state tracking on location change
    setHasReceivedInitialState(false);

    const checkChatPanelState = () => {
      if ((window as any).__chatPanelControls?.isOpen) {
        const isOpen = (window as any).__chatPanelControls.isOpen();
        setIsChatPanelOpen(isOpen);
        setHasReceivedInitialState(true);
      }
    };

    // Check immediately
    checkChatPanelState();

    // Set up an interval to check chat panel state
    const interval = setInterval(checkChatPanelState, 100);

    return () => clearInterval(interval);
  }, [location.pathname]);

  // Handle chat toggle
  const handleChatToggle = () => {
    if ((window as any).__chatPanelControls) {
      (window as any).__chatPanelControls.toggle();
    }
  };

  // Library toolbar handlers
  const handleViewChange = (newView: LibraryViewType) => {
    setView(newView);
    setTag({ key: 'library-view-type', value: newView });
  };

  const handleDownload = () => {
    const fileCount = fileArray.length;
    dispatch(
      setAlertMessage({
        type: 'info',
        message: `Starting download of ${fileCount} file${fileCount > 1 ? 's' : ''}...`,
      }),
    );
    dispatch(downloadMultipleFiles(fileArray));
  };

  const handleMove = () => {
    dispatch(pushModal({ type: 'move-file-modal', files: fileArray }));
  };

  const handleDelete = () => {
    dispatch(pushModal({ type: 'delete-files-modal', files: fileArray }));
  };

  const handleDeselect = () => {
    dispatch(deselectAllFiles());
    if (setSelectMode) {
      setSelectMode(false);
    }
  };

  if (isLiveView) return null;

  return (
    <header className={cn('w-full bg-background')}>
      <div className="flex h-14 items-center px-2">
        <div className="flex w-full items-center justify-between gap-2">
          {/* Hamburger menu - show only when sidebar is in offcanvas mode and closed */}
          {shouldShowHamburger && (
            <SidebarTrigger icon="menu" className="h-10 w-10 p-0" />
          )}

          {/* Avatar List - show in selected video view or folder view */}
          {isSelectedVideoView && selectedVideo.file && (
            <div className="flex-shrink-0">
              <AccessAvatarList item={selectedVideo.file} size="sm" />
            </div>
          )}
          {shouldShowAccessList && (
            <div className="flex-shrink-0">
              <AccessAvatarList folder={currentFolder || undefined} size="sm" />
            </div>
          )}

          {/* Assistant Toolbar - hidden but still rendered */}
          <div className="hidden">
            <AssistantToolbar onFocusChange={setIsAssistantFocused} />
          </div>

          {/* Spacer with optional share prompt for newly created files */}
          <SharePromptSpacer
            show={isSelectedVideoView && isNewlyCreatedFile}
            onShareClick={handleShareClick}
          />

          {/* Action Buttons - right aligned with consistent spacing */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Library toolbar elements - show in folder view when library has files */}
            {isLibraryFolderView && hasLibraryFiles && (
              <>
                {/* Sort button (hidden when selecting files) */}
                {!showSelectedFileActions && (
                  <SortComponent>
                    <button
                      type="button"
                      className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 px-2.5 text-muted-foreground hover:bg-muted-foreground/20 hover:text-foreground transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
                    >
                      <ArrowDownUp className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                  </SortComponent>
                )}

                {/* View toggle (hidden when selecting files) */}
                {!showSelectedFileActions && (
                  <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
                    <ToggleGroup
                      type="single"
                      value={view}
                      onValueChange={(value: string) =>
                        value && handleViewChange(value as LibraryViewType)
                      }
                      className="gap-1 relative"
                    >
                      <ToggleGroupItem
                        value="grid"
                        aria-label="Grid View"
                        size="sm"
                        className="h-7 px-2 rounded-md transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm hover:text-foreground hover:bg-background/50"
                      >
                        <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
                        <span className="sr-only">Grid</span>
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="list"
                        aria-label="List View"
                        size="sm"
                        className="h-7 px-2 rounded-md transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm hover:text-foreground hover:bg-background/50"
                      >
                        <List className="h-4 w-4" strokeWidth={1.75} />
                        <span className="sr-only">List</span>
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                )}

                {/* Selected file actions (shown when files are selected) */}
                {showSelectedFileActions && (
                  <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDownload}
                      className="h-7 px-2 rounded-md text-muted-foreground"
                    >
                      <Download className="h-4 w-4" strokeWidth={1.75} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleMove}
                      className="h-7 px-2 rounded-md text-muted-foreground"
                    >
                      <FolderInput className="h-4 w-4" strokeWidth={1.75} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDelete}
                      className="h-7 px-2 rounded-md text-muted-foreground"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                    </Button>
                    <button
                      type="button"
                      onClick={handleDeselect}
                      className="h-7 px-2 flex items-center justify-center"
                    >
                      <X
                        className="h-4 w-4 text-muted-foreground"
                        strokeWidth={1.75}
                      />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Share button - show only in selected video view */}
            {isSelectedVideoView && selectedVideo.file && canShare ? (
              <Button
                variant="secondary"
                size="default"
                onClick={() => {
                  setIsToolbarShareDialogOpen(true);
                }}
                className="h-10 px-3 shadow-sm transition-all duration-300 ease-in-out bg-white dark:bg-secondary hover:bg-white/90 dark:hover:bg-secondary/80"
                aria-label={t('common.share')}
              >
                <Share className="mr-1 h-4 w-4" />
                {t('common.share')}
              </Button>
            ) : null}

            <UpsellButton />
            <EducationalDiscount />
            <UpgradeButton />

            {/* Live view chat toggle button - only show when chat panel is closed */}
            {isLiveView && livePanel && livePanel.rightPanelCollapsed && (
              <button
                type="button"
                onClick={() => livePanel.setRightPanelCollapsed(false)}
                className="group inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 px-2.5 text-muted-foreground hover:bg-muted-foreground/20 hover:text-foreground hover:cursor-e-resize transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
                aria-label="Toggle chat"
              >
                <MessageCircle className="h-4 w-4 stroke-[1.75]" />
              </button>
            )}

            {/* Chat toggle button - only show when chat panel is closed */}
            {/* Hide during initial load in selected video view to prevent flicker */}
            {!isLiveView &&
              !isChatPanelOpen &&
              (hasReceivedInitialState || !shouldChatPanelBeOpenByDefault) && (
                <button
                  type="button"
                  id="feature-ai-chat"
                  onClick={handleChatToggle}
                  className="group inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 px-2.5 text-muted-foreground hover:bg-muted-foreground/20 hover:text-foreground hover:cursor-w-resize transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
                  aria-label="Toggle chat"
                >
                  <MessageCircle className="h-4 w-4 stroke-[1.75] group-hover:hidden" />
                  <PanelLeftClose className="h-4 w-4 stroke-[1.75] hidden group-hover:block" />
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Toolbar Share Dialog - opens in parent/simple view for video files only */}
      <ShareDialog
        open={isToolbarShareDialogOpen}
        onOpenChange={setIsToolbarShareDialogOpen}
      />
    </header>
  );
};

export default CustomToolbar;
